import React, { useState } from "react";
import {
  Calendar,
  Clock,
  User,
  Mail,
  Phone,
  MapPin,
  MessageSquare,
  CheckCircle,
  Flame,
  AlertCircle,
  Shield,
  Award,
} from "lucide-react";
import { ServiceTypes, TimeSlots } from "../constants.jsx";

const RequestAppointment = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    serviceType: "",
    otherServiceDescription: "",
    preferredDate: "",
    preferredTime: "",
    message: "",
  });

  const [formStatus, setFormStatus] = useState(null); // null, 'success', 'error'
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch("/.netlify/functions/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setFormStatus("success");
        setFormData({
          name: "",
          email: "",
          phone: "",
          address: "",
          serviceType: "",
          otherServiceDescription: "",
          preferredDate: "",
          preferredTime: "",
          message: "",
        });

        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        setFormStatus("error");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setFormStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 min-h-screen py-24 sm:py-28 lg:py-36 overflow-hidden">
      {/* Enhanced animated glow effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-transparent to-orange-600/10" />
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-orange-500/10 rounded-full blur-[150px] glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-orange-600/10 rounded-full blur-[150px] glow-pulse-delayed" />
      </div>

      {/* Enhanced grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:32px_32px]" />

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-5xl px-6 lg:px-8">
        {/* Header - More compact and professional */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-orange-500/20 to-orange-600/20 border border-orange-500/30 px-5 py-2.5 mb-8 shadow-lg shadow-orange-500/10">
            <Calendar className="h-5 w-5 text-orange-400" />
            <span className="text-sm font-semibold text-orange-300">
              Professional Gas Services
            </span>
          </div>
          <h1 className="text-5xl font-bold tracking-tight text-white sm:text-6xl lg:text-7xl mb-6">
            Schedule Your{" "}
            <span className="bg-gradient-to-r from-orange-400 via-orange-300 to-amber-300 bg-clip-text text-transparent">
              Service
            </span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Complete the form below and we'll contact you within 24 hours to
            confirm your appointment.
          </p>
        </div>

        {/* Two-column layout for larger screens */}
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          {/* Left sidebar - Trust elements */}
          <div className="lg:col-span-1 space-y-6">
            {/* Why Choose Us Card */}
            <div className="bg-gradient-to-br from-slate-800/90 to-slate-800/70 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 shadow-xl">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <Shield className="h-6 w-6 text-orange-400" />
                Why Choose Us
              </h3>
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-orange-400" />
                  </div>
                  <div>
                    <div className="font-semibold text-white text-sm mb-1">
                      Licensed & Insured
                    </div>
                    <div className="text-xs text-slate-400">
                      Fully certified professionals
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
                    <Clock className="h-5 w-5 text-orange-400" />
                  </div>
                  <div>
                    <div className="font-semibold text-white text-sm mb-1">
                      Same-Day Service
                    </div>
                    <div className="text-xs text-slate-400">
                      Fast response times
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
                    <Award className="h-5 w-5 text-orange-400" />
                  </div>
                  <div>
                    <div className="font-semibold text-white text-sm mb-1">
                      15+ Years Experience
                    </div>
                    <div className="text-xs text-slate-400">
                      Trusted by thousands
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Card */}
            <div className="bg-gradient-to-br from-orange-600/20 to-orange-700/20 backdrop-blur-sm border border-orange-500/30 rounded-2xl p-6 shadow-xl">
              <div className="text-center">
                <Phone className="h-12 w-12 text-orange-400 mx-auto mb-4" />
                <h4 className="font-bold text-white text-lg mb-2">
                  Need to Talk?
                </h4>
                <p className="text-sm text-orange-200/80 mb-4">
                  Prefer to discuss your service needs over the phone?
                </p>
                <a
                  href="tel:9805406064"
                  className="inline-flex items-center justify-center gap-2 w-full bg-white hover:bg-orange-50 text-orange-600 font-bold px-6 py-3 rounded-lg transition-all duration-200 hover:scale-105 shadow-lg"
                >
                  <Phone className="h-5 w-5" />
                  (980) 540-6064
                </a>
                <p className="text-xs text-orange-200/60 mt-3">
                  Mon-Fri: 8AM-6PM
                </p>
              </div>
            </div>
          </div>

          {/* Form Card - Main column */}
          <div className="lg:col-span-2">
            <div className="bg-slate-800/90 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-2xl p-8 lg:p-10">
              {formStatus === "success" && (
                <div className="mb-8 flex items-start gap-3 bg-green-500/10 border border-green-500/30 rounded-xl p-5 shadow-lg">
                  <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-green-400 text-lg mb-1">
                      Request Submitted!
                    </h3>
                    <p className="text-sm text-green-300/90">
                      We'll contact you within 24 hours to confirm your
                      appointment.
                    </p>
                  </div>
                </div>
              )}

              {formStatus === "error" && (
                <div className="mb-8 flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-xl p-5 shadow-lg">
                  <AlertCircle className="h-6 w-6 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-red-400 text-lg mb-1">
                      Something Went Wrong
                    </h3>
                    <p className="text-sm text-red-300/90">
                      Please try again or call us at (980) 540-6064.
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-7">
                {/* Personal Information */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Name */}
                  <div>
                    <label
                      htmlFor="name"
                      className="flex items-center gap-2 text-sm font-bold text-white mb-2.5"
                    >
                      <User className="h-4 w-4 text-orange-400" />
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 bg-slate-900/60 border border-slate-600/60 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all hover:border-slate-500"
                      placeholder="John Smith"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label
                      htmlFor="phone"
                      className="flex items-center gap-2 text-sm font-bold text-white mb-2.5"
                    >
                      <Phone className="h-4 w-4 text-orange-400" />
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 bg-slate-900/60 border border-slate-600/60 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all hover:border-slate-500"
                      placeholder="(980) 540-6064"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="flex items-center gap-2 text-sm font-bold text-white mb-2.5"
                  >
                    <Mail className="h-4 w-4 text-orange-400" />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 bg-slate-900/60 border border-slate-600/60 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all hover:border-slate-500"
                    placeholder="john.smith@example.com"
                  />
                </div>

                {/* Address */}
                <div>
                  <label
                    htmlFor="address"
                    className="flex items-center gap-2 text-sm font-bold text-white mb-2.5"
                  >
                    <MapPin className="h-4 w-4 text-orange-400" />
                    Service Address *
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    required
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 bg-slate-900/60 border border-slate-600/60 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all hover:border-slate-500"
                    placeholder="123 Main Street, City, State, ZIP"
                  />
                </div>

                {/* Service Type */}
                <div>
                  <label
                    htmlFor="serviceType"
                    className="flex items-center gap-2 text-sm font-bold text-white mb-2.5"
                  >
                    <Flame className="h-4 w-4 text-orange-400" />
                    Service Needed *
                  </label>
                  <select
                    id="serviceType"
                    name="serviceType"
                    required
                    value={formData.serviceType}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 bg-slate-900/60 border border-slate-600/60 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all hover:border-slate-500 cursor-pointer appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27rgb(148 163 184)%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3e%3cpolyline points=%276 9 12 15 18 9%27%3e%3c/polyline%3e%3c/svg%3e')] bg-[length:1.25rem] bg-[right_1rem_center] bg-no-repeat pr-12"
                  >
                    <option value="" className="bg-slate-900 text-slate-400">
                      Select a service
                    </option>
                    {ServiceTypes.map((service) => (
                      <option
                        key={service}
                        value={service}
                        className="bg-slate-900 text-white"
                      >
                        {service}
                      </option>
                    ))}
                  </select>

                  {/* Custom input for "Other" service type */}
                  {formData.serviceType === "Other" && (
                    <div className="mt-3 animate-fade-in">
                      <input
                        type="text"
                        id="otherServiceDescription"
                        name="otherServiceDescription"
                        required
                        value={formData.otherServiceDescription}
                        onChange={handleChange}
                        className="w-full px-4 py-3.5 bg-slate-900/60 border border-orange-500/60 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all hover:border-orange-400"
                        placeholder="Please describe the service you need"
                      />
                    </div>
                  )}
                </div>

                {/* Date and Time */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Preferred Date */}
                  <div>
                    <label
                      htmlFor="preferredDate"
                      className="flex items-center gap-2 text-sm font-bold text-white mb-2.5"
                    >
                      <Calendar className="h-4 w-4 text-orange-400" />
                      Preferred Date *
                    </label>
                    <input
                      type="date"
                      id="preferredDate"
                      name="preferredDate"
                      required
                      value={formData.preferredDate}
                      onChange={handleChange}
                      min={new Date().toISOString().split("T")[0]}
                      className="w-full px-4 py-3.5 bg-slate-900/60 border border-slate-600/60 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all hover:border-slate-500 cursor-pointer"
                    />
                  </div>

                  {/* Preferred Time */}
                  <div>
                    <label
                      htmlFor="preferredTime"
                      className="flex items-center gap-2 text-sm font-bold text-white mb-2.5"
                    >
                      <Clock className="h-4 w-4 text-orange-400" />
                      Preferred Time *
                    </label>
                    <select
                      id="preferredTime"
                      name="preferredTime"
                      required
                      value={formData.preferredTime}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 bg-slate-900/60 border border-slate-600/60 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all hover:border-slate-500 cursor-pointer appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27rgb(148 163 184)%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3e%3cpolyline points=%276 9 12 15 18 9%27%3e%3c/polyline%3e%3c/svg%3e')] bg-[length:1.25rem] bg-[right_1rem_center] bg-no-repeat pr-12"
                    >
                      <option value="" className="bg-slate-900 text-slate-400">
                        Select a time
                      </option>
                      {TimeSlots.map((slot) => (
                        <option
                          key={slot}
                          value={slot}
                          className="bg-slate-900 text-white"
                        >
                          {slot}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Additional Message */}
                <div>
                  <label
                    htmlFor="message"
                    className="flex items-center gap-2 text-sm font-bold text-white mb-2.5"
                  >
                    <MessageSquare className="h-4 w-4 text-orange-400" />
                    Additional Details (Optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 bg-slate-900/60 border border-slate-600/60 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all resize-none hover:border-slate-500"
                    placeholder="Please provide any additional information about your service needs..."
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full inline-flex items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-8 py-4 text-lg font-bold text-white shadow-xl hover:shadow-orange-500/40 hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 hover:from-orange-600 hover:to-orange-700"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="h-5 w-5 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                        Submitting Request...
                      </>
                    ) : (
                      <>
                        <Calendar className="h-6 w-6" />
                        Submit Appointment Request
                      </>
                    )}
                  </button>
                </div>

                {/* Privacy Notice */}
                <p className="text-xs text-slate-500 text-center pt-2">
                  Your information is secure and will never be shared with third
                  parties
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes glowPulse {
          0%, 100% {
            opacity: 0.4;
          }
          50% {
            opacity: 0.7;
          }
        }
        
        @keyframes glowPulseDelayed {
          0%, 100% {
            opacity: 0.3;
          }
          50% {
            opacity: 0.6;
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .glow-pulse {
          animation: glowPulse 5s ease-in-out infinite;
        }
        
        .glow-pulse-delayed {
          animation: glowPulseDelayed 6s ease-in-out infinite;
          animation-delay: 1s;
        }

        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }

        /* Enhance date picker styling */
        input[type="date"]::-webkit-calendar-picker-indicator {
          cursor: pointer;
          filter: invert(0.6);
          background-image: url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27rgb(148 163 184)%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3e%3crect x=%273%27 y=%274%27 width=%2718%27 height=%2718%27 rx=%272%27 ry=%272%27%3e%3c/rect%3e%3cline x1=%2716%27 y1=%272%27 x2=%2716%27 y2=%276%27%3e%3c/line%3e%3cline x1=%278%27 y1=%272%27 x2=%278%27 y2=%276%27%3e%3c/line%3e%3cline x1=%273%27 y1=%2710%27 x2=%2721%27 y2=%2710%27%3e%3c/line%3e%3c/svg%3e');
          width: 1.25rem;
          height: 1.25rem;
        }

        input[type="date"]::-webkit-calendar-picker-indicator:hover {
          filter: invert(0.8);
        }
      `}</style>
    </div>
  );
};

export default RequestAppointment;
